import { BehaviorSubject, Observable, take, filter, forkJoin, of, catchError, throwError, timeout } from 'rxjs';
import socketStateEnum from './SocketStateEnum.mjs';
import mseLogTypeEnum from './MseLogTypeEnum.mjs';

/**
 * @module Media.MseVideoService
 */
function createMseVideoService(apiService, logService, environment, _window, util) {
    const moduleName = 'mseVideoService';
    const mimeCodec = 'video/mp4; codecs="avc1.42E01F"'; //'video/mp4; codecs="avc1.42E01E'   Baseline@L3.1 => 42E01F
    const mseDevices = {};
    const bufferMaxSec = 1.5;
    const createMediaSessionTimeoutMs = 5000;
    const createWebSocketTimeoutMs = 5000;

    function log(params) {
        params.module = moduleName;
        logService.log(params);
        return params;
    }

    function initMediaSource(mseDevice) {
        mseDevice.mediaSource.addEventListener('sourceopen', function() {
            mseDevice.sourceBuffer = mseDevice.mediaSource.addSourceBuffer(mimeCodec);
            mseDevice.sourceBuffer.addEventListener(
                'updateend',
                function() {
                    if (mseDevice?.queue.length && !mseDevice?.videoElement.error) {
                        mseDevice?.sourceBuffer.appendBuffer(mseDevice?.queue.shift());
                        skipFowardIfNecessary(mseDevice);
                    }
                },
                false
            );
            mseDevice.sourceBuffer.addEventListener(
                'error',
                function(error) {
                    if (mseDevice) {
                        mseDevice.info.next({
                            type: mseLogTypeEnum.sourceBufferError,
                            deviceId: mseDevice.deviceId,
                            error: error,
                            state: mseDevice?.sourceBuffer?.readyState,
                            messageIndex: mseDevice.messageIndex,
                            module: moduleName,
                            timeSinceStart: _window.Date.now() - (mseDevice?.startTime ?? 0),
                            traceId: mseDevice.traceId
                        });
                        log({
                            code: mseLogTypeEnum.sourceBufferError,
                            deviceId: mseDevice.deviceId,
                            object: {
                                state: mseDevice?.sourceBuffer?.readyState,
                                messageIndex: mseDevice.messageIndex,
                                timeSinceStart: _window.Date.now() - (mseDevice?.startTime ?? 0)
                            },
                            int: _window.Date.now() - (mseDevice?.startTime ?? 0),
                            error: error,
                            traceId: mseDevice.traceId
                        });
                    }
                },
                false
            );
        });
    }

    // this may cause problems in Android, but the main use case is desktop browsers
    function skipFowardIfNecessary(mseDevice) {
        if (mseDevice?.sourceBuffer?.buffered?.length > 0 && mseDevice?.videoElement) {
            const bufferSec = mseDevice.sourceBuffer.buffered.end(mseDevice.sourceBuffer.buffered.length - 1)
                - mseDevice.videoElement.currentTime;

            // slow down video playback according to delay, if any
            if (mseDevice.delay > 0 && bufferSec < mseDevice.delay) {
                mseDevice.videoElement.currentTime -= Math.min(mseDevice.delay - bufferSec + 0.05, mseDevice.videoElement.currentTime);
                return true;
            }

            // speed up video playback if the buffer is too big
            if (bufferSec > bufferMaxSec + mseDevice.delay) {
                mseDevice.videoElement.currentTime += bufferSec - (bufferMaxSec + mseDevice.delay) + 0.05;
                return true;
            }
        }
        return false;
    }

    /**
     * @description handles messages from the WebSocket
     * @this {WebSocket} The WebSocket that called the function
     * @param {Object} e
     * @param {String} e.data either binary media or a string message
     */
    function onWebSocketMessage(e) {
        if (e.data instanceof ArrayBuffer) {
            onBinary(this.deviceId, e.data);
        } else {
            onText(this.deviceId, e.data);
        }
    }

    /**
     * @description Called when there is an error with a WebSocket sometime after socket startup
     *     and after some messages have been sent.
     * @this {WebSocket} The WebSocket that called the function
     * @param {Error} error Research shows the error value returned
     *     by the browser is mostly useless or non-existant
     */
    function onWebSocketError(error) {
        const mseDevice = mseDevices[this.deviceId];
        // Only log and emit if the messageIndex exists and is greater than 0.
        // When messageIndex is 0, socketOpenFailed will emit and log, making this redundant.
        // The socketOpenFailed case is handled by initStreamWebSocketError.
        if (mseDevice?.messageIndex) {
            mseDevice?.info.error({
                type: mseLogTypeEnum.socketError,
                error: error,
                deviceId: this?.deviceId,
                module: moduleName,
                messageIndex: mseDevice?.messageIndex,
                traceId: mseDevice?.traceId,
                timeSinceStart: _window.Date.now() - (mseDevice?.startTime ?? 0)
            });
            log({
                code: mseLogTypeEnum.socketError,
                deviceId: mseDevice?.deviceId,
                object: {
                    messageIndex: mseDevice?.messageIndex,
                    e: error,
                    timeSinceStart: _window.Date.now() - (mseDevice?.startTime ?? 0)
                },
                error: error,
                traceId: mseDevice?.traceId
            });
        }
        removeMseDevice(mseDevice);
    }

    /**
     * @description start playing automatically if possible, or if that didn't work,
     *     then autoplay is not supported or the user needs to give
     *     or has not given permission
     */
    function handleAutoPlay(mseDevice) {
        if (mseDevice?.videoElement?.paused) {
            try {
                mseDevice?.videoElement?.play().then(
                    () => {
                        mseDevice?.info.next({
                            type: mseLogTypeEnum.autoPlay,
                            deviceId: mseDevice?.deviceId,
                            value: true,
                            module: moduleName,
                            traceId: mseDevice?.traceId
                        });
                    },
                    () => {
                        mseDevice?.info.next({
                            type: mseLogTypeEnum.autoPlay,
                            deviceId: mseDevice?.deviceId,
                            value: false,
                            module: moduleName,
                            traceId: mseDevice?.traceId
                        });
                    }
                );
            } catch (error) {
                mseDevice?.info.next({
                    type: mseLogTypeEnum.autoPlay,
                    deviceId: mseDevice?.deviceId,
                    value: false,
                    traceId: mseDevice?.traceId,
                    error: error,
                    module: moduleName
                });
            }
        }
    }

    function onBinaryHandleFirstPacket(mseDevice) {
        if (!mseDevice.receivedFirstPacket) {
            mseDevice.receivedFirstPacket = true;
            log({
                code: mseLogTypeEnum.firstPacketReceived,
                type: 'info',
                int: _window.Date.now() - (mseDevice?.startTime ?? 0),
                object: {
                    messageIndex: mseDevice?.messageIndex
                },
                deviceId: mseDevice?.deviceId,
                traceId: mseDevice?.traceId
            });
            mseDevice.info.next({
                type: mseLogTypeEnum.firstPacketReceived,
                deviceId: mseDevice.deviceId,
                module: moduleName,
                traceId: mseDevice?.traceId,
                timeSinceStart: _window.Date.now() - (mseDevice?.startTime ?? 0),
                messageIndex: mseDevice?.messageIndex
            });
        }
    }

    function onBinaryHandleBufferError(mseDevice, error) {
        let errorCode;
        if (error?.code === MediaError.MEDIA_ERR_ABORTED) {
            errorCode = mseLogTypeEnum.mediaErrorAborted;
        } else if (error?.code === MediaError.MEDIA_ERR_NETWORK) {
            errorCode = mseLogTypeEnum.mediaErrorNetwork;
        } else if (error?.code === MediaError.MEDIA_ERR_DECODE) {
            errorCode = mseLogTypeEnum.mediaErrorDecode;
        } else if (error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
            errorCode = mseLogTypeEnum.mediaErrorSrcNotSupported;
        } else {
            errorCode = mseLogTypeEnum.mediaError;
        }

        log({
            code: errorCode,
            int: _window.Date.now() - (mseDevice?.startTime ?? 0),
            error: error,
            object: {
                messageIndex: mseDevice?.messageIndex,
                code: error?.code,
                message: error?.message
            },
            deviceId: mseDevice?.deviceId,
            traceId: mseDevice?.traceId
        });

        mseDevice.info.error({
            type: errorCode,
            error: error,
            deviceId: mseDevice?.deviceId,
            module: moduleName,
            timeSinceStart: _window.Date.now() - (mseDevice?.startTime ?? 0),
            traceId: mseDevice?.traceId,
            messageIndex: mseDevice?.messageIndex,
            code: error?.code,
            message: error?.message,
            // Let the client know the most recent video data that was tried
            // since it could be corrupted or improperly formatted.
            // Clients may save this data for analysis.
            latestVideoData: mseDevice?.latestVideoData
        });

        removeMseDevice(mseDevice);
    }

    function onBinaryHandleBuffer(mseDevice) {
        mseDevice.latestVideoData = mseDevice.queue.shift();
        mseDevice.sourceBuffer.appendBuffer(mseDevice.latestVideoData);
        skipFowardIfNecessary(mseDevice);
        mseDevice.info.next({
            type: mseLogTypeEnum.info,
            messageSize: mseDevice?.latestVideoData?.byteLength,
            messageIndex: mseDevice.messageIndex,
            deviceId: mseDevice.deviceId,
            t: _window.Date.now(),
            module: moduleName,
            traceId: mseDevice?.traceId,
            latestVideoData: mseDevice.latestVideoData
        });
    }

    function onBinary(deviceId, buffer) {
        const mseDevice = mseDevices[deviceId];
        const socket = mseDevice?.socket;
        const videoElement = mseDevice?.videoElement;
        if (socket && videoElement && buffer?.byteLength > 0) {
            onBinaryHandleFirstPacket(mseDevice);
            mseDevice.messageIndex += 1;
            handleAutoPlay(mseDevice);
            mseDevice.queue.push(new Uint8Array(buffer));
            if (!mseDevice.sourceBuffer.updating && !videoElement.paused) {
                if (!videoElement.error) {
                    onBinaryHandleBuffer(mseDevice);
                } else {
                    onBinaryHandleBufferError(mseDevice, videoElement?.error);
                }
            }
        }
    }

    function onText(deviceId, messageString) {
        try {
            const messageObject = JSON.parse(messageString);
            // The server returns {"status":200} when the WebSocket has been successfully
            // prepared to stream
            const mseDevice = mseDevices[deviceId];
            if (messageObject.status === 200 && mseDevice) {
                mseDevice.info.next({
                    type: mseLogTypeEnum.socketInitConfirmed,
                    deviceId: deviceId,
                    module: moduleName
                });

                util.startMedia(
                    mseDevice,
                    apiService,
                    mseLogTypeEnum,
                    log,
                    'VIDEO'
                );
            }
        } catch (error) {
            const mseDevice = mseDevices[deviceId];
            mseDevice?.info?.next({
                type: mseLogTypeEnum.badServerResponse,
                deviceId: deviceId,
                module: moduleName,
                traceId: mseDevice?.traceId
            });
            log({
                code: mseLogTypeEnum.badServerResponse,
                deviceId: deviceId,
                object: {
                    messageIndex: mseDevice?.messageIndex
                },
                int: _window.Date.now() - (mseDevice?.startTime ?? 0),
                error: error,
                traceId: mseDevice?.traceId
            });
        }
    }

    /**
     * @description called when a WebSocket closes
     * {@link https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/close_event}
     * @this {WebSocket} The WebSocket that called the function
     * @param {Object} event info from the browser
     * @param {Number} event.code
     * @param {String} event.reason
     * @param {Boolean} event.wasClean
     */
    function onWebSocketClose(event) {
        const mseDevice = mseDevices[this.deviceId];
        const socket = mseDevice?.socket;
        if (socket) {
            if (socket.videoElement) {
                socket.videoElement.src = '';
            }
            socket.queue?.splice(0);
            socket.removeEventListener('error', onWebSocketError);
            socket.removeEventListener('open', onWebSocketOpen);
            socket.removeEventListener('close', onWebSocketClose);
            socket.removeEventListener('message', onWebSocketMessage);
        }
        mseDevice?.info.next({
            type: mseLogTypeEnum.socketClosed,
            deviceId: mseDevice?.deviceId,
            module: moduleName,
            traceId: mseDevice?.traceId,
            event: event
        });
        // event.code==1000 is the normal closure code, so do not log
        // event.code==1006 seems to be an internal browser error which
        // probably happens when the server closes the socket due to inactivity.
        if (event?.code !== 1000) {
            log({
                code: mseLogTypeEnum.socketClosed,
                deviceId: mseDevice?.deviceId,
                int: _window.Date.now() - (mseDevice?.startTime ?? 0),
                object: {
                    code: event.code,
                    reason: event.reason,
                    wasClean: event.wasClean,
                    messageIndex: mseDevice?.messageIndex
                },
                traceId: mseDevice?.traceId
            });
        }
        mseDevice?.info.complete();

        try {
            _window.URL.revokeObjectURL(mseDevice?.objectUrl);
        } catch (ignore) {
            // Tried to clear the memory but failed. Not a big deal - just keep going.
        }

        delete mseDevices[this.deviceId];
    }

    function onWebSocketOpen() {
        const mseDevice = mseDevices?.[this.deviceId];
        mseDevice?.info.next({
            type: mseLogTypeEnum.socketOpenConfirmed,
            deviceId: this.deviceId,
            module: moduleName,
            traceId: mseDevice?.traceId
        });
        if (mseDevice?.socket?.killOnOpen) {
            // The user requested the socket to be closed before it opened,
            // so close it now
            removeMseDevice(mseDevice);
        }
    }

    function sendCommand(deviceId, command) {
        if (mseDevices[deviceId]) {
            const socket = mseDevices[deviceId].socket;
            if (socket && socket.readyState === socketStateEnum.open) {
                socket.send(command);
            }
        }
    }

    /**
     * @description Deletes an mseDevice.
     *     Be sure to error or complete the info BehaviorSubject before calling this.
     * @param {String} deviceId
     */
    function removeMseDevice(mseDevice) {
        if (mseDevice) {
            _window.clearInterval(mseDevice.keepAlive);
            mseDevice.keepAlive = undefined;
            if (mseDevice?.socket?.readyState === socketStateEnum.connecting) {
                // Closing a socket in the connecting state throws a warning at least in some browsers.
                // Setting the killOnOpen flag will close the socket after it opens.
                mseDevice.socket.killOnOpen = true;
            } else {
                // 1000 is a normal closure.
                // https://www.rfc-editor.org/rfc/rfc6455.html#section-7.1.6
                mseDevice?.socket?.close(1000);
            }
        }
    }

    function createSocket(mseDevice) {
        try {
            let socket = new _window.WebSocket(environment.webSocketBase + '/cxs/ws/video');
            socket.deviceId = mseDevice?.deviceId;
            socket.binaryType = 'arraybuffer';
            socket.addEventListener('error', onWebSocketError);
            socket.addEventListener('open', onWebSocketOpen);
            socket.addEventListener('close', onWebSocketClose);
            socket.addEventListener('message', onWebSocketMessage);
            mseDevice.socket = socket;
            mseDevice.info.next({
                type: mseLogTypeEnum.socketCreated,
                deviceId: mseDevice?.deviceId,
                module: moduleName,
                traceId: mseDevice?.traceId
            });
        } catch (error) {
            mseDevice.info.error({
                type: mseLogTypeEnum.socketCreateFailed,
                error: error,
                deviceId: mseDevice?.deviceId,
                module: moduleName,
                timeSinceStart: _window.Date.now() - (mseDevice?.startTime ?? 0),
                traceId: mseDevice?.traceId
            });
            log({
                code: mseLogTypeEnum.socketCreateFailed,
                deviceId: mseDevice.deviceId,
                int: _window.Date.now() - (mseDevice?.startTime ?? 0),
                object: {
                    messageIndex: mseDevice?.messageIndex
                },
                error: error,
                traceId: mseDevice?.traceId
            });
            removeMseDevice(mseDevice);
        }
    }

    function socketIsOpen(value) {
        return value && value.type === mseLogTypeEnum.socketOpenConfirmed;
    }

    function initStream(mseDevice) {

        createSocket(mseDevice);

        // Open a viewerSession and a socket in parallel for best performance,
        // then send the init command to start streaming
        forkJoin({
            viewerSession: util.createMediaSession(
                mseDevice,
                apiService,
                mseLogTypeEnum
            ).pipe(
                timeout({
                    each: createMediaSessionTimeoutMs,
                    with: () => throwError(() => new Error('create viewerSession timed out'))
                }),
                catchError(e => of({ isError: true, error: e }))
            ),
            newSocket: mseDevice.info.pipe(
                filter(socketIsOpen),
                take(1),
                timeout({
                    each: createWebSocketTimeoutMs,
                    with: () => throwError(() => new Error('create WebSocket timed out'))
                }),
                catchError(e => of({ isError: true, error: e }))
            )
        }).subscribe({
            next(result) {
                if (result?.viewerSession?.isError) {
                    initStreamViewerSessionError(mseDevice, result.viewerSession.error);
                    return;
                }
                if (result?.newSocket?.isError) {
                    initStreamWebSocketError(mseDevice, result.newSocket.error);
                    return;
                }

                // initialize the stream
                const command = {
                    path: '/init',
                    sid: result.viewerSession,
                    deviceId: mseDevice.deviceId,
                    traceId: mseDevice.traceId
                };
                sendCommand(mseDevice.deviceId, JSON.stringify(command));
                mseDevice.info.next({
                    type: mseLogTypeEnum.socketInitRequested,
                    deviceId: mseDevice.deviceId,
                    module: moduleName,
                    traceId: mseDevice?.traceId
                });
                mseDevice.keepAlive = util.getKeepalive(mseDevice, apiService, mseLogTypeEnum);
            }
        });
    }

    function initStreamWebSocketError(mseDevice, webSocketError) {
        mseDevice.info.error({
            type: mseLogTypeEnum.socketOpenFailed,
            error: webSocketError,
            deviceId: mseDevice?.deviceId,
            timeSinceStart: _window.Date.now() - (mseDevice?.startTime ?? 0),
            module: moduleName,
            traceId: mseDevice?.traceId
        });
        log({
            code: mseLogTypeEnum.socketOpenFailed,
            deviceId: mseDevice?.deviceId,
            int: _window.Date.now() - (mseDevice?.startTime ?? 0),
            object: {
                messageIndex: mseDevice?.messageIndex
            },
            error: webSocketError,
            traceId: mseDevice?.traceId
        });
        removeMseDevice(mseDevice);
    }

    function initStreamViewerSessionError(mseDevice, viewerSessionError) {
        // inform the client why the session failed
        if (viewerSessionError?.response?.status === 401) {
            // user was using bad credentials
            mseDevice.info.error({
                type: mseLogTypeEnum.notAuthorized,
                error: viewerSessionError,
                deviceId: mseDevice?.deviceId,
                module: moduleName,
                timeSinceStart: _window.Date.now() - (mseDevice?.startTime ?? 0),
                traceId: mseDevice?.traceId
            });
            log({
                code: mseLogTypeEnum.notAuthorized,
                deviceId: mseDevice?.deviceId,
                int: _window.Date.now() - (mseDevice?.startTime ?? 0),
                object: {
                    messageIndex: mseDevice?.messageIndex
                },
                error: viewerSessionError,
                traceId: mseDevice?.traceId
            });
            removeMseDevice(mseDevice);
            return;
        }

        if (Array.isArray(viewerSessionError?.response?.data?.errors)) {
            let handledOffline = false;
            viewerSessionError.response.data.errors.forEach(e => {
                if (e.code === 'camera_offline') {
                    // the device was actually offline
                    mseDevice.info.error({
                        type: mseLogTypeEnum.offline,
                        error: viewerSessionError,
                        deviceId: mseDevice?.deviceId,
                        module: moduleName,
                        timeSinceStart: _window.Date.now() - (mseDevice?.startTime ?? 0),
                        traceId: mseDevice?.traceId
                    });
                    log({
                        code: mseLogTypeEnum.offline,
                        deviceId: mseDevice?.deviceId,
                        int: _window.Date.now() - (mseDevice?.startTime ?? 0),
                        object: {
                            messageIndex: mseDevice?.messageIndex
                        },
                        error: viewerSessionError,
                        traceId: mseDevice?.traceId
                    });
                    handledOffline = true;
                }
            });
            if (handledOffline) {
                removeMseDevice(mseDevice);
                return;
            }
        }

        // some other error happened besides notAuthorized or offline
        mseDevice.info.error({
            type: mseLogTypeEnum.viewerSessionFailed,
            error: viewerSessionError,
            deviceId: mseDevice?.deviceId,
            module: moduleName,
            timeSinceStart: _window.Date.now() - (mseDevice?.startTime ?? 0),
            traceId: mseDevice?.traceId
        });
        log({
            code: mseLogTypeEnum.viewerSessionFailed,
            deviceId: mseDevice?.deviceId,
            int: _window.Date.now() - (mseDevice?.startTime ?? 0),
            object: {
                messageIndex: mseDevice?.messageIndex
            },
            error: viewerSessionError,
            traceId: mseDevice?.traceId
        });
        removeMseDevice(mseDevice);
    }

    /**
     * {@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/error}
     * @param {MediaError} e
     */
    function onVideoError(e) {
        const mseDevice = mseDevices[this.deviceId];
        mseDevice?.info.next({
            type: mseLogTypeEnum.videoError,
            deviceId: mseDevice?.deviceId,
            error: e,
            module: moduleName,
            playerCode: mseDevice?.videoElement?.error?.code,
            playerMessage: mseDevice?.videoElement?.error?.message,
            timeSinceStart: _window.Date.now() - (mseDevice?.startTime ?? 0),
            traceId: mseDevice?.traceId
        });
        log({
            code: mseLogTypeEnum.videoError,
            deviceId: mseDevice?.deviceId,
            object: {
                playerCode: mseDevice?.videoElement?.error?.code,
                playerMessage: mseDevice?.videoElement?.error?.message,
                timeSinceStart: _window.Date.now() - (mseDevice?.startTime ?? 0),
                receivedFirstPacket: mseDevice?.receivedFirstPacket,
                messageIndex: mseDevice?.messageIndex
            },
            error: e,
            int: mseDevice?.videoElement.error.code,
            traceId: mseDevice?.traceId
        });
    }

    function onVideoPlaying() {
        const mseDevice = mseDevices[this.deviceId];
        mseDevice?.info?.next({
            type: mseLogTypeEnum.videoPlaying,
            deviceId: mseDevice.deviceId,
            traceId: mseDevice.traceId
        });
    }

    // ****************************
    // public members
    // ****************************

    /**
     * @property {Boolean} isSupported True if the client supports MSE Video playback
     * @name isSupported
     */
    function isSupported() {
        return Boolean(_window.MediaSource);
    }

    /**
     * @description Start MSE livestreaming
     * @param {String} deviceId
     * @param {HTMLVideoElement} videoElement The video element to display video in
     * @param {String} [traceId]
     * @returns {Observable} Emits info about streaming states
     *     that clients can use for logging and interacting with video.
     *     Values will always be an object with a type property of type MseLogTypeEnum
     * @method
     * @name start
     */
    function start(deviceId, videoElement, traceId) {
        if (!deviceId || typeof deviceId !== 'string') {
            return new Observable(subscriber => subscriber.error(
                log({
                    code: 'startBadDeviceId',
                    traceId: traceId,
                    module: moduleName,
                    deviceId: deviceId,
                    object: videoElement
                })
            ));
        }
        if (!util.isValidVideoElement(videoElement)) {
            return new Observable(subscriber => subscriber.error(
                log({
                    code: 'startBadVideoElement',
                    traceId: traceId,
                    module: moduleName,
                    deviceId: deviceId,
                    object: videoElement
                })
            ));
        }
        if (!mseDevices[deviceId]) {
            mseDevices[deviceId] = {
                socket: undefined,
                info: new BehaviorSubject({
                    type: mseLogTypeEnum.init,
                    deviceId: deviceId
                }),
                deviceId: deviceId,
                videoElement: videoElement,
                mediaSource: new _window.MediaSource(),
                sourceBuffer: undefined,
                queue: [],
                delay: 0,
                messageIndex: 0,
                keepAlive: undefined,
                viewerSession: undefined,
                receivedFirstPacket: undefined,
                startTime: _window.Date.now(),
                traceId: traceId ?? util.randomBase64Code(15),
                latestVideoData: undefined,
                objectUrl: undefined
            };
            mseDevices[deviceId].videoElement.addEventListener('error', onVideoError);
            mseDevices[deviceId].videoElement.addEventListener('playing', onVideoPlaying);
            // the setTimeout forces the BehaviorSubject to emit its first value mseLogTypeEnum.init
            // before doing anything else
            _window.setTimeout(
                () => {
                    initStream(mseDevices[deviceId]);
                    initMediaSource(mseDevices[deviceId]);
                    mseDevices[deviceId].objectUrl = _window.URL.createObjectURL(mseDevices[deviceId].mediaSource);
                    mseDevices[deviceId].videoElement.src = mseDevices[deviceId].objectUrl;
                },
                1
            );
        }
        return mseDevices[deviceId].info.asObservable();
    }

    /**
     * @description Stops livestreaming and closes the related socket
     * @param {String} deviceId
     * @returns {Observable} Completes when the socket is closed
     * @method
     * @name stop
     */
    function stop(deviceId) {
        if (!deviceId || typeof deviceId !== 'string') {
            return new Observable(subscriber => {
                subscriber.error({
                    type: mseLogTypeEnum.badParams,
                    module: moduleName
                });
            });
        }
        const mseDevice = mseDevices[deviceId];
        if (mseDevice) {
            removeMseDevice(mseDevice);
            return mseDevice.info.asObservable()
                .pipe(filter(x => x.type === mseLogTypeEnum.socketClosed), take(1));
        }
        return new Observable(subscriber => {
            subscriber.error({
                type: mseLogTypeEnum.badParams,
                module: moduleName,
                deviceId: deviceId
            });
        });
    }

    /**
     * @param {String} deviceId The device to set extra delay for
     * @param {Number} value Seconds of extra delay
     * @method
     * @name setDelay
     */
    function setDelay(deviceId, value) {
        if (mseDevices[deviceId]) {
            if (value === undefined || value === null || !isFinite(value)) {
                mseDevices[deviceId].delay = 0;
            } else {
                mseDevices[deviceId].delay = value;
            }
        }
    }

    return Object.freeze({
        /** @property {module:MseVideoService.MseLogTypeEnum} logTypeEnum */
        get logTypeEnum() { return mseLogTypeEnum; },
        get isSupported() { return isSupported(); },
        start: start,
        stop: stop,
        setDelay: setDelay
    });
}

export default createMseVideoService;
